import { jsx as _jsx } from "react/jsx-runtime";
import { useState, forwardRef, useMemo } from 'react';
import { Provider } from 'react-redux';
import invariant from 'react-utils/invariant';
import { createStore } from '../../redux/createStore';
import { StoreContext } from '../../redux/storeContext';
import { EmptyComponent } from '../utils/EmptyComponent';
import { DEFAULT_PAGE_SIZES } from '../../pagination/constants/pageSizes';
import emptyFunction from 'react-utils/emptyFunction';
import Table from './Table';
import { ProvideAuth } from '../../auth/context/AuthContext';
import { COLUMN_MINIMUM_WIDTH, DEFAULT_COLUMN_WIDTH } from '../constants/sizes'; // @ts-expect-error This library is not yet typed.

import SharedDragDropContext from 'customer-data-ui-utilities/dnd/SharedDragDropContext';
import { DefaultRowComponent } from './DefaultRowComponent';
import { DefaultTableComponent } from './DefaultTableComponent';
import { ProvideLogger } from '../../logger/context/LoggerContext';
import DefaultSelectAllComponent from '../../selection/components/DefaultSelectAllComponent';
const emptyArray = [];
const DEFAULT_NAMESPACE = 'DEFAULT_NAMESPACE';

const TableWrapper = ({
  getRowId,
  hasScope,
  hasGate,
  rows,
  columns,
  totalRowCount = 0,
  loading = false,
  resizable = true,
  reorderable = true,
  selectable = true,
  pageable = true,
  pageIndex = 0,
  pageSize = DEFAULT_PAGE_SIZES[0],
  pageSizes = DEFAULT_PAGE_SIZES,
  sortable = true,
  numberOfFrozenColumns = 0,
  sorts = emptyArray,
  BulkActionsComponent = EmptyComponent,
  RowComponent = DefaultRowComponent,
  TableComponent = DefaultTableComponent,
  headerLeftSlot = null,
  headerRightSlot = null,
  noRowsSlot = null,
  onColumnWidthChange = emptyFunction,
  onColumnOrderChange = emptyFunction,
  onPageIndexChange = emptyFunction,
  onPageSizeChange = emptyFunction,
  onSortsChange = emptyFunction,
  canSelectAllRecords = true,
  logger = emptyFunction,
  CustomSelectAllComponent = DefaultSelectAllComponent
}, __ref) => {
  // This ensures that the reference is stable as long as the component is mounted.
  // useMemo may throw away a reference in the future so it should not be used here.
  const [store] = useState(createStore);
  const columnsWithDefaults = useMemo(() => columns.map(column => {
    const width = column.width || DEFAULT_COLUMN_WIDTH;
    const minimumColumnWidth = column.minimumColumnWidth || COLUMN_MINIMUM_WIDTH;
    invariant(width >= minimumColumnWidth, `A column was provided with conflicting minimumColumnWidth and width properties.` + '\n' + `(columnName = "${column.columnName}", columnNamespace = "${column.columnNamespace}", width = "${width}", minimumColumnWidth = "${minimumColumnWidth}" )`);
    return Object.assign({}, column, {
      columnNamespace: column.columnNamespace || DEFAULT_NAMESPACE,
      width,
      minimumColumnWidth,
      // The single != is load-bearing! We do not want to override column-level settings.
      resizable: column.resizable != null ? column.resizable : resizable,
      reorderable: column.reorderable != null ? column.reorderable : reorderable,
      sortable: column.sortable != null ? column.sortable : sortable
    });
  }), [columns, reorderable, resizable, sortable]);
  const sortsWithDefaults = useMemo(() => sorts.map(sort => Object.assign({}, sort, {
    columnNamespace: sort.columnNamespace != null ? sort.columnNamespace : DEFAULT_NAMESPACE
  })), [sorts]);
  return /*#__PURE__*/_jsx(ProvideAuth, {
    hasScope: hasScope,
    hasGate: hasGate,
    children: /*#__PURE__*/_jsx(ProvideLogger, {
      logger: logger,
      children: /*#__PURE__*/_jsx(Provider, {
        store: store,
        context: StoreContext,
        children: /*#__PURE__*/_jsx(Table, {
          getRowId: getRowId,
          rows: rows,
          columns: columnsWithDefaults,
          sorts: sortsWithDefaults,
          resizable: resizable,
          reorderable: reorderable,
          selectable: selectable,
          pageable: pageable,
          sortable: sortable,
          loading: loading,
          pageIndex: pageIndex,
          pageSize: pageSize,
          pageSizes: pageSizes,
          totalRowCount: totalRowCount,
          BulkActionsComponent: BulkActionsComponent,
          RowComponent: RowComponent,
          TableComponent: TableComponent,
          headerLeftSlot: headerLeftSlot,
          headerRightSlot: headerRightSlot,
          noRowsSlot: noRowsSlot,
          onColumnWidthChange: onColumnWidthChange,
          onColumnOrderChange: onColumnOrderChange,
          onPageIndexChange: onPageIndexChange,
          onPageSizeChange: onPageSizeChange,
          onSortsChange: onSortsChange,
          canSelectAllRecords: canSelectAllRecords,
          numberOfFrozenColumns: numberOfFrozenColumns,
          CustomSelectAllComponent: CustomSelectAllComponent
        })
      })
    })
  });
};

export default SharedDragDropContext()( /*#__PURE__*/forwardRef(TableWrapper));